import { UseQueryResult } from '@tanstack/react-query'
import React from 'react'

import ClientResults from '@/components/misc/GlobalSearch/components/ClientResults'
import CompanyResults from '@/components/misc/GlobalSearch/components/CompanyResults'
import CompassSnapshotResults from '@/components/misc/GlobalSearch/components/CompassSnapshotResults'
import NoResultsMessage from '@/components/misc/GlobalSearch/components/NoResultsMessage'
import ReferralResults from '@/components/misc/GlobalSearch/components/ReferralResults'
import UserCompanyResults from '@/components/misc/GlobalSearch/components/UserCompanyResults'
import { useGlobalSearch } from '@/components/misc/GlobalSearch/hooks/useGlobalSearch'
import { cn } from '@/lib/utils'
import { GlobalSearchResultsItem } from '@/types/responses/companies'

interface Props {
  globalSearchQuery: UseQueryResult<GlobalSearchResultsItem>
  /** The typed term is ahead of the (debounced) term the query ran for. */
  isDebouncing: boolean
}

const GlobalSearchResults: React.FC<Props> = ({
  globalSearchQuery,
  isDebouncing,
}) => {
  const { searchTerm } = useGlobalSearch()
  const results = globalSearchQuery.data

  if (
    !searchTerm ||
    // Mid-debounce the query still holds the previous term's results (which may
    // be freshly cached, so no loading flag is set) — hide them until the query
    // catches up, otherwise old results flash before the new ones load.
    isDebouncing ||
    globalSearchQuery.isFetching ||
    globalSearchQuery.isPending ||
    globalSearchQuery.isLoading ||
    globalSearchQuery.isPlaceholderData
  ) {
    return null
  }

  if (globalSearchQuery.error) {
    throw globalSearchQuery.error
  }

  if (!results || results.totalItems === 0) {
    return <NoResultsMessage />
  }

  const companies = [
    ...results.registeredCompanies,
    ...results.panelInviteCompanies,
  ]

  // Each group's results pop in after the groups above it
  const clientsIndexOffset = results.referrals.length
  const compassSnapshotsIndexOffset =
    clientsIndexOffset + results.clients.length
  const companiesIndexOffset =
    compassSnapshotsIndexOffset + results.compassSnapshots.length
  const userCompaniesIndexOffset = companiesIndexOffset + companies.length

  return (
    <div
      className={cn(
        'transition-all origin-top',
        globalSearchQuery.isFetching && 'opacity-50 scale-95',
      )}
    >
      <ReferralResults referrals={results.referrals} />
      <ClientResults
        clients={results.clients}
        indexOffset={clientsIndexOffset}
      />
      <CompassSnapshotResults
        compassSnapshots={results.compassSnapshots}
        indexOffset={compassSnapshotsIndexOffset}
      />
      <CompanyResults
        companies={companies}
        indexOffset={companiesIndexOffset}
      />
      <UserCompanyResults
        companies={companies}
        indexOffset={userCompaniesIndexOffset}
      />
    </div>
  )
}

export default GlobalSearchResults
